// @ts-strict-ignore
import { startTransition, useEffect, useMemo, useRef, useState } from 'react'
import { isPrerendering } from 'services/prerender'
import { detectTimezoneName, detectTimezoneOffset } from 'services/timeKeeping'
import { useSelector } from 'hooks/redux'
import { getBuildID } from 'services/versioning'
import { isE2ETest } from 'services/browserAgent'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { utility_GetCurrentUserQuery } from '__generated__/utility_GetCurrentUserQuery.graphql'
import { useTheme } from '@mui/material/styles'

/**
 * @deprecated use useEffect(callback, []) instead
 * @param callback callback to execute only once
 */
export function useOnMount(callback: () => any) {
  const [init, setInit] = useState(false)
  if (!init) {
    setInit(true)
    callback()
  }
}

export interface IIsPrerenderingOptions {
  useTransition?: boolean
}

/**
 * A hook to determine whether the current render pass is being done through SSR (server-side rendering).
 *
 * **Important note: for components rendered on the server, this hook will return true for the first render on the client as well.**
 * **This is to avoid any hydration mismatches between server and client rendering, and will cause an additional re-render initially for such components.**
 *
 * @returns A boolean indicating whether the current render pass is SSR.
 */
export function useIsPrerendering(options: IIsPrerenderingOptions = {}) {
  const shouldUseTransition = options.useTransition ?? true
  const [clientRendered, setClientRendered] = useState(false) // TODO: default this to 'true' if it's known that the hook is used near 'useClientOnlyRender'

  useEffect(() => {
    if (shouldUseTransition) {
      startTransition(() => {
        setClientRendered(true)
      })
    } else {
      setClientRendered(true)
    }
  }, [shouldUseTransition]) // only runs on the client, after hydration (or standalone initial rendering)

  return !clientRendered
}

export function useIsE2ETest() {
  const prerendering = useIsPrerendering()
  return useMemo(() => isE2ETest(), [prerendering])
}

export function useDetectTimezone() {
  return useMemo(() => ({ name: detectTimezoneName(), offset: detectTimezoneOffset() }), [])
}

export function useInterval(timeMs: number, callback: () => void, enable = true) {
  const [intervalId, setIntervalId] = useState(null as any)
  useEffect(() => {
    if (enable) {
      if (!intervalId) setIntervalId(setInterval(callback, timeMs))
    } else {
      clearInterval(intervalId)
    }
    return () => clearInterval(intervalId)
  }, [callback, enable, intervalId, timeMs])
}

/**
 * Get the current build id for the frontend
 *
 * NOTE: can suspend
 */
export function useFrontendVersion() {
  const user = useLazyLoadQuery<utility_GetCurrentUserQuery>(graphql`
    query utility_GetCurrentUserQuery {
      currentUser {
        configuration {
          frontendBuildId
        }
      }
    }
  `, {}, {
    fetchPolicy: 'store-or-network',
  })
  // TODO: serverBuildId won't receive realtime updates due to legacy websocket being removed
  // Connect to the GraphQL subscription to receive those updates
  const serverBuildId = useSelector(state => state.common.serverBuildId)
  const lastBuildID = user?.currentUser?.configuration?.frontendBuildId || null
  const buildId = getBuildID()
  const updateAvailable = !isPrerendering() && buildId && lastBuildID && (buildId !== lastBuildID || (serverBuildId && serverBuildId !== buildId))

  return {
    updateAvailable,
  }
}

/**
 * @deprecated this hook was created to transition old components to hooks. The components using it should be
 * refactored to not depend on this hook anymore, then this hook should be deleted
 */
export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

/**
 * @deprecated this hook was created to transition old components to hooks. The components using it should be
 * refactored to not depend on this hook anymore, then this hook should be deleted
 */
export function useForceUpdate() {
  const [value, setValue] = useState(0)
  return () => setValue(value + 1)
}

export function useSidebarBreakpoint() {
  const theme = useTheme()
  return theme.breakpoints.values.xl
}
